import cx from 'classnames';
import { useMemo, useRef } from 'react';
import type { FunctionComponent, ReactNode } from 'react';

import type { PanelNames } from '@trello/split-screen';
import {
  useSplitScreenResize,
  useSplitScreenSharedState,
} from '@trello/split-screen';
import { getTestId } from '@trello/test-ids';
import type { SplitScreenTestIds } from '@trello/test-ids/';

import styles from './CollapsibleSplitScreenPanel.less';

interface CollapsibleSplitScreenPanelProps {
  children?: ReactNode;
  panelName: PanelNames;
  isHidden?: boolean;
  panelWidth?: number;
  enableTransitions?: boolean;
  isCollapsed?: boolean;
}

export const CollapsibleSplitScreenPanel: FunctionComponent<
  CollapsibleSplitScreenPanelProps
> = ({
  children,
  isHidden,
  panelName,
  panelWidth,
  enableTransitions,
  isCollapsed,
}) => {
  const { isRightmostPanel } = useSplitScreenResize();

  const { areMultiplePanelsOpen } = useSplitScreenSharedState();
  const panelRef = useRef<HTMLDivElement>(null);

  const isRightmost = isRightmostPanel(panelName);

  const panelFlexValue = useMemo(() => {
    if (isHidden || isCollapsed) {
      return '0 0 0px';
    }

    if (panelName === 'inbox' && areMultiplePanelsOpen) {
      return `0 0 ${panelWidth || 0}px`;
    } else if (panelName === 'inbox' && !areMultiplePanelsOpen) {
      return `1 1 ${panelWidth || 0}px`;
    }

    return `1 1 ${panelWidth || 0}px`;
  }, [isHidden, isCollapsed, panelName, areMultiplePanelsOpen, panelWidth]);

  return (
    <>
      <div
        className={cx(styles.panel, styles[panelName], {
          [styles.singlePanel]: !areMultiplePanelsOpen,
          [styles.panelTransitions]: enableTransitions,
          [styles.rightmostPanel]: isRightmost,
          [styles.hidden]: isHidden,
          [styles.collapsed]: isCollapsed,
        })}
        data-testid={getTestId<SplitScreenTestIds>('split-screen-panel')}
        style={{
          flex: panelFlexValue,
        }}
        ref={panelRef}
        aria-hidden={isHidden}
      >
        {children}
      </div>
    </>
  );
};
